/**
 * SavedWorkspacesPanel — the saved-workspace library: list, load into this
 * thread, delete. Shared by the file tree and the VS Code view.
 */
import { useCallback, useEffect, useState, type FC } from 'react';
import { BookmarkCheck, Loader2, Trash2, X } from 'lucide-react';
import { api } from '@/services/api';
import { formatSize, formatTs } from './workspace-format';

interface SharedWorkspaceMeta {
  id: string;
  name: string;
  description: string;
  fileCount: number;
  totalSize: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

interface SavedWorkspacesPanelProps {
  threadId: string;
  onClose: () => void;
  /** A saved workspace was loaded into this thread. */
  onLoaded: () => void;
  onError: (message: string) => void;
}

export const SavedWorkspacesPanel: FC<SavedWorkspacesPanelProps> = ({ threadId, onClose, onLoaded, onError }) => {
  const [savedWorkspaces, setSavedWorkspaces] = useState<SharedWorkspaceMeta[]>([]);
  const [savedWsLoading, setSavedWsLoading] = useState(false);
  const [savedWsActionId, setSavedWsActionId] = useState<string | null>(null);

  const fetchSavedWorkspaces = useCallback(async () => {
    setSavedWsLoading(true);
    try {
      const data = await api.get<SharedWorkspaceMeta[]>('/chat/shared-workspaces');
      setSavedWorkspaces(data);
    } catch (err) {
      console.warn('[Workspace] Failed to list saved workspaces:', err);
    } finally {
      setSavedWsLoading(false);
    }
  }, []);

  useEffect(() => { fetchSavedWorkspaces(); }, [fetchSavedWorkspaces]);

  const handleLoadSavedWorkspace = useCallback(async (wsId: string, wsName: string) => {
    if (!confirm(`Load "${wsName}" into this workspace? Existing files with the same names will be overwritten.`)) return;
    setSavedWsActionId(wsId);
    try {
      await api.post(`/chat/shared-workspaces/${wsId}/load`, { threadId });
      onLoaded();
      onClose();
    } catch (err) {
      console.warn('[Workspace] Load shared workspace failed:', err);
      onError('Failed to load workspace — please try again');
    } finally {
      setSavedWsActionId(null);
    }
  }, [threadId, onLoaded, onClose, onError]);

  const handleDeleteSavedWorkspace = useCallback(async (wsId: string, wsName: string) => {
    if (!confirm(`Delete saved workspace "${wsName}"? This cannot be undone.`)) return;
    setSavedWsActionId(wsId);
    try {
      await api.delete(`/chat/shared-workspaces/${wsId}`);
      setSavedWorkspaces((prev) => prev.filter((w) => w.id !== wsId));
    } catch (err) {
      console.warn('[Workspace] Delete shared workspace failed:', err);
      onError('Failed to delete workspace — please try again');
    } finally {
      setSavedWsActionId(null);
    }
  }, [onError]);

  return (
    <div className="border-b border-zinc-200 bg-zinc-50/50 shrink-0 max-h-[50%] overflow-auto">
      <div className="px-3 py-2 flex items-center justify-between border-b border-zinc-100">
        <span className="text-xs font-semibold text-zinc-600">Saved Workspaces</span>
        <button onClick={onClose} className="p-0.5 rounded hover:bg-zinc-200">
          <X className="w-3 h-3 text-zinc-400" />
        </button>
      </div>
      {savedWsLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-4 h-4 text-zinc-600 animate-spin" />
        </div>
      ) : savedWorkspaces.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <BookmarkCheck className="w-8 h-8 text-zinc-200 mx-auto mb-2" />
          <p className="text-xs text-zinc-400">No saved workspaces yet</p>
          <p className="text-xs text-zinc-400 mt-1">Ask the AI to save your workspace</p>
        </div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {savedWorkspaces.map((ws) => (
            <div key={ws.id} className="px-3 py-2.5 hover:bg-white transition-colors group">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-zinc-700 truncate">{ws.name}</p>
                  {ws.description && (
                    <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{ws.description}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1 text-xs text-zinc-400">
                    <span>{ws.fileCount} file{ws.fileCount !== 1 ? 's' : ''}</span>
                    <span>{formatSize(ws.totalSize)}</span>
                    <span>{formatTs(ws.createdAt)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  {savedWsActionId === ws.id ? (
                    <Loader2 className="w-3.5 h-3.5 text-zinc-600 animate-spin" />
                  ) : (
                    <>
                      <button
                        onClick={() => handleLoadSavedWorkspace(ws.id, ws.name)}
                        className="text-xs px-2 py-1 rounded-md bg-zinc-100 text-zinc-600 hover:bg-zinc-100 transition-colors font-medium"
                      >
                        Load
                      </button>
                      <button
                        onClick={() => handleDeleteSavedWorkspace(ws.id, ws.name)}
                        className="p-1 rounded-md hover:bg-red-50 text-zinc-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
